import React from "react";
import { Icon } from "./registry";
/* ============================================================
   Fence Works — "what the software did for you"
   The owner should never have to wonder whether this is paying for itself.
   Everything here is computed from what the store already records — quote
   sources, sends, online payments, reviews, AI calls, the automation
   toggles — never estimated from thin air. Where a number is a rule of
   thumb (hours saved) it says so.
   • roiSummary(store, days)  — the numbers
   • RoiPanel                 — the dashboard card
   • AutopilotStrip           — To-Do: which automations are on, who they reach
   • nextTouch                — when the autopilot's next message goes out
   • WeekDigest               — Reports: the Monday-morning summary
   ============================================================ */

const money0 = (n: number) => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");
const WON = new Set(["signed", "paid"]);
const DAY = 86400000;

/* Who each automation reaches, in the owner's words. Keyed by the AI Front
   Desk card id (ai.automations[id]). */
const AUTOMATION_WHO: Record<string, { name: string; who: string; icon: string }> = {
  missed_call_text:   { name: "Missed-call text",      who: "anyone whose call you miss, within seconds", icon: "phone" },
  speed_to_lead:      { name: "Speed-to-lead",         who: "every new website / widget lead, instantly", icon: "bolt" },
  quote_followup:     { name: "Quote follow-up",       who: "unsigned quotes at 1, 3 and 7 days", icon: "send" },
  abandoned_quote:    { name: "Abandoned quote",       who: "visitors who priced a fence and left", icon: "map" },
  estimate_confirm:   { name: "Estimate confirmation", who: "customers with a booked site visit", icon: "calendar" },
  appt_reminder:      { name: "Appointment reminder",  who: "customers the day before a visit", icon: "clock" },
  appt_confirm:       { name: "Appointment confirm",   who: "customers asked to confirm the visit", icon: "check" },
  job_status:         { name: "Job status texts",      who: "customers when the crew is on the way / done", icon: "bolt" },
  review_request:     { name: "Review request",        who: "every customer, the day after completion", icon: "check" },
  review_interceptor: { name: "Review interceptor",    who: "unhappy customers, before they post", icon: "lock" },
  photo_capture:      { name: "After-photo capture",   who: "the crew at completion, then the customer", icon: "image" },
  ar_followup:        { name: "Balance reminders",     who: "customers with an overdue balance", icon: "dollar" },
  financing_prequal:  { name: "Financing pre-qual",    who: "customers on quotes over your threshold", icon: "dollar" },
  attach_upsell:      { name: "Add-on offers",         who: "booked jobs (gates, stain, caps)", icon: "box" },
  reschedule_weather: { name: "Weather reschedule",    who: "you and the crew when rain hits an install day", icon: "calendar" },
  permit_reminder:    { name: "Permit reminder",       who: "you, when a booked job needs a permit", icon: "book" },
  material_reorder:   { name: "Low-stock reorder",     who: "you, when a paid job drops stock below par", icon: "box" },
  smart_dispatch:     { name: "Smart dispatch",        who: "the crew with the open day nearest the job", icon: "user" },
  owner_summary:      { name: "Owner summary",         who: "you, every morning", icon: "mail" },
  web_chat:           { name: "Website chat",          who: "visitors on your site, 24/7", icon: "chat" },
  bilingual:          { name: "Bilingual answering",   who: "Spanish-speaking callers", icon: "phone" },
  lead_parser:        { name: "Lead-email parser",     who: "leads that arrive by email (Angi, Thumbtack…)", icon: "mail" },
  review_replies:     { name: "Review replies",        who: "every new Google review", icon: "chat" },
  review_share:       { name: "Review sharing",        who: "your social pages, when a 5★ lands", icon: "target" },
  referral_nudge:     { name: "Referral ask",          who: "the customer, right after their 5★ review", icon: "user" },
};

function activeAutomations(store: any) {
  const ai = (store && store.settings && store.settings.ai) || {};
  const autos = ai.automations || {};
  return Object.keys(AUTOMATION_WHO).filter(id => !!autos[id]).map(id => ({ id, ...AUTOMATION_WHO[id] }));
}

/* the numbers — `days` = 0 means all time */
function roiSummary(store: any, days = 30) {
  const since = days > 0 ? Date.now() - days * DAY : 0;
  const quotes = (store.quotes || []).filter((q: any) => (Number(q.createdAt) || 0) >= since || days === 0);
  const inWin = (t: any) => days === 0 || (Number(t) || 0) >= since;
  // 1) leads the system brought in (widget, booking page, Lead Radar) and what they became
  const sysLeads = quotes.filter((q: any) => q.source === "widget" || q.source === "booking" || q.source === "leadradar");
  const sysWon = sysLeads.filter((q: any) => WON.has(q.status));
  const sysWonTotal = sysWon.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
  // 2) money collected online (Stripe deposits / balances)
  let online = 0, onlineCount = 0;
  (store.quotes || []).forEach((q: any) => (Array.isArray(q.stripePayments) ? q.stripePayments : []).forEach((p: any) => { if (inWin(p.at)) { online += Number(p.amount) || 0; onlineCount++; } }));
  // 3) follow-ups that went out, and quotes that closed after more than one touch
  let touches = 0;
  (store.quotes || []).forEach((q: any) => (Array.isArray(q.sends) ? q.sends : []).forEach((s: any) => { if (inWin(s.at)) touches++; }));
  const wonAfterFollowup = (store.quotes || []).filter((q: any) => WON.has(q.status) && Array.isArray(q.sends) && q.sends.length > 1 && inWin(q.sends[q.sends.length - 1].at));
  const wonAfterFollowupTotal = wonAfterFollowup.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
  // 4) calls the AI answered (recorded call log)
  const ai = (store.settings && store.settings.ai) || {};
  const recept = ai.receptionist || {};
  const calls = recept.enabled && Array.isArray(recept.calls) ? recept.calls : [];
  const booked = calls.filter((c: any) => /book/i.test(String(c.outcome || ""))).length;
  // 5) reviews
  const wos = store.workOrders || [];
  const reviewsAsked = wos.filter((w: any) => w.reviewRequested && inWin(w.reviewRequestedAt)).length;
  const reviews = wos.filter((w: any) => w.reviewReceived && inWin(w.reviewReceivedAt || w.reviewRequestedAt)).length;
  // 6) time — a rule of thumb, labelled as one: a satellite estimate replaces a
  //    45-minute site measure; a sent quote replaces 15 minutes of paperwork;
  //    every automated touch replaces 5 minutes of texting
  const estimates = (store.projects || []).filter((p: any) => inWin(p.createdAt)).length;
  const sent = quotes.filter((q: any) => Array.isArray(q.sends) && q.sends.length).length;
  const hours = Math.round((estimates * 0.75 + sent * 0.25 + touches * (5 / 60)) * 10) / 10;
  const automations = activeAutomations(store);
  const plan = (window as any).VFP_PLAN && (window as any).VFP_PLAN.get ? (window as any).VFP_PLAN.get(store) : null;
  const planPrice = plan && Number(plan.full) > 0 ? Number(plan.full) : 0;   // PLANS[].full = list price / mo
  const attributed = sysWonTotal + wonAfterFollowupTotal;
  return { days, sysLeads: sysLeads.length, sysWon: sysWon.length, sysWonTotal, online, onlineCount, touches, wonAfterFollowup: wonAfterFollowup.length, wonAfterFollowupTotal, calls: calls.length, booked, reviewsAsked, reviews, estimates, sent, hours, automations, planPrice, attributed, roi: planPrice > 0 ? attributed / planPrice : 0 };
}

/* Calls the contractor's website brought in (call tracking, server/src/sites/
   calltracking.js): only with the Growth plan's website, a service and a
   tracked number. null = nothing to show. `days` 0 (all time) reads a year. */
function useWebsiteCalls(store: any, days: number) {
  const [c, setC] = React.useState<any>(null);
  const growth = !!((window as any).VFP_PLAN && (window as any).VFP_PLAN.allows(store, "website"));
  React.useEffect(() => {
    const DB: any = (window as any).VFP_DB;
    if (!(window as any).VFP_API_BASE || !growth || !DB || !DB.siteCallSummary) { setC(null); return; }
    let alive = true;
    DB.siteCallSummary(days || 365).then((r: any) => { if (alive) setC(r && (r.tracking || r.total) ? r : null); }).catch(() => { if (alive) setC(null); });
    return () => { alive = false; };
  }, [days, growth]);
  return c;
}
/* "11 calls from your website · 9 answered, 2 missed" */
function websiteCallsLine(c: { total: number; answered: number; missed: number } | null) {
  if (!c) return "";
  const n = Number(c.total) || 0;
  return `${n} call${n === 1 ? "" : "s"} from your website` + (n ? ` · ${c.answered} answered, ${c.missed} missed` : "");
}

/* ---- Dashboard card ---- */
function RoiPanel({ store, go, compact }: any) {
  // compact = the cancel-flow mirror: all time, no period tabs, and the list
  // of automations that would stop — the whole case for staying, on one card
  const [days, setDays] = React.useState<any>(compact ? 0 : 30);
  const r = React.useMemo(() => roiSummary(store, days), [store, days]);
  const webCalls = useWebsiteCalls(store, days);
  const empty = !r.sysLeads && !r.online && !r.touches && !r.calls && !r.reviews && !r.estimates;
  return (
    <div className="card roi-card">
      <div className="row between" style={{ flexWrap: "wrap", gap: 8, alignItems: "baseline" }}>
        <div>
          <span className="kicker kicker-teal">{compact ? "Since you started" : "What Fence Works did for you"}</span>
          <h2 className="display" style={{ fontSize: 20, margin: "6px 0 0" }}>
            {empty ? "Your first estimate starts the clock." : r.attributed > 0 ? `${money0(r.attributed)} in won work it brought in or closed` : `${r.hours} hrs of office work handled`}
          </h2>
        </div>
        {!compact && <div className="cat-tabs" style={{ margin: 0 }}>
          {[[30, "30 days"], [90, "90 days"], [0, "All time"]].map(([d, lb]: any) => (
            <button key={d} className={"filter-chip" + (days === d ? " on" : "")} onClick={() => setDays(d)}>{lb}</button>
          ))}
        </div>}
      </div>
      <div className="roi-grid">
        <button className="roi-tile" onClick={() => go("estimates")} title="Quotes that came in through your website widget, booking page or Lead Radar, and closed">
          <b className="tnum">{money0(r.sysWonTotal)}</b><span>won from {r.sysLeads} lead{r.sysLeads === 1 ? "" : "s"} the system brought in</span>
        </button>
        <button className="roi-tile" onClick={() => go("estimates")} title="Quotes that signed after more than one follow-up went out">
          <b className="tnum">{money0(r.wonAfterFollowupTotal)}</b><span>closed after a follow-up · {r.touches} touch{r.touches === 1 ? "" : "es"} sent</span>
        </button>
        <button className="roi-tile" onClick={() => go("integrations")} title="Deposits and balances paid online through the customer hub">
          <b className="tnum">{money0(r.online)}</b><span>collected online · {r.onlineCount} payment{r.onlineCount === 1 ? "" : "s"}</span>
        </button>
        <button className="roi-tile" onClick={() => go("ai")} title="Calls the AI receptionist answered">
          <b className="tnum">{r.calls}</b><span>call{r.calls === 1 ? "" : "s"} answered by AI · {r.booked} booked</span>
        </button>
        <button className="roi-tile" onClick={() => go("workorders")} title="Google reviews collected by the automatic request">
          <b className="tnum">{r.reviews}</b><span>review{r.reviews === 1 ? "" : "s"} collected · {r.reviewsAsked} asked</span>
        </button>
        <button className="roi-tile" onClick={() => go("reports")} title="Rule of thumb: 45 min per satellite estimate, 15 min per quote sent, 5 min per automated touch">
          <b className="tnum">{r.hours} h</b><span>office hours saved · {r.estimates} estimate{r.estimates === 1 ? "" : "s"}, {r.sent} sent</span>
        </button>
      </div>
      {webCalls && (
        <button className="roi-line" onClick={() => go("website", { tab: "leads" })} title="Calls to your website's tracked number">
          <Icon name="phone" size={13} /><span>{websiteCallsLine(webCalls)}</span><Icon name="chevright" size={12} />
        </button>
      )}
      <div className="roi-foot">
        {r.planPrice > 0 && r.attributed > 0
          ? <span><b style={{ color: "var(--teal)" }}>{Math.round(r.roi)}× your plan</b> — {money0(r.attributed)} attributed against {money0(r.planPrice)}/mo.</span>
          : <span className="muted">{r.automations.length} automation{r.automations.length === 1 ? "" : "s"} running for you.</span>}
        {!compact && <button className="btn btn-ghost btn-sm" onClick={() => go("ai")}>{r.automations.length ? "See what's running" : "Turn on automations"}<Icon name="chevright" size={13} /></button>}
      </div>
      {compact && r.automations.length > 0 && (
        <div className="autopilot-list" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
          <span className="kicker" style={{ color: "var(--amber)" }}>These stop the day you cancel</span>
          {r.automations.map(a => <span key={a.id} className="autopilot-item"><Icon name={a.icon} size={12} /><b>{a.name}</b><span className="muted"> → {a.who}</span></span>)}
        </div>
      )}
    </div>
  );
}

/* ---- next automated touch for a lead (the To-Do's "Autopilot" line) ----
   The cadence is the quote follow-up's: day 1, 3 and 7 after the last touch.
   Speed-to-lead covers a brand-new lead within minutes. */
function nextTouch(store: any, f: any) {
  const ai = (store.settings && store.settings.ai) || {};
  const autos = ai.automations || {};
  const live = !!((window as any).VFP_API_BASE);
  if (f.isNew && autos.speed_to_lead) return { on: true, text: "Autopilot: reply text goes out within minutes" };
  if (!autos.quote_followup) return { on: false, text: "Autopilot off — turn on quote follow-up" };
  if (!live) return { on: true, text: "Autopilot on · sends once the service is connected" };
  const ds = f.daysSince == null ? 0 : f.daysSince;
  const next = [1, 3, 7].find(d => d > ds);
  if (next == null) return { on: true, text: "Autopilot: cadence done — your turn" };
  const at = new Date(Date.now() + (next - ds) * DAY); at.setHours(9, 0, 0, 0);
  const label = at.toDateString() === new Date().toDateString() ? "today 9am" : at.toLocaleDateString("en-US", { weekday: "short" }) + " 9am";
  return { on: true, text: "Autopilot: next text " + label + " (touch " + Math.min(3, ((f.touches || 0) + 1)) + " of 3)" };
}

/* ---- To-Do strip: what's running for you ---- */
function AutopilotStrip({ store, go }: any) {
  const on = activeAutomations(store);
  const all = Object.keys(AUTOMATION_WHO).length;
  return (
    <div className="autopilot-strip">
      <div className="row between" style={{ flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <span className="kicker">Running for you · {on.length} of {all}</span>
        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={() => go("ai")}>{on.length ? "Manage" : "Turn on"}<Icon name="chevright" size={12} /></button>
      </div>
      {on.length === 0
        ? <p className="muted" style={{ fontSize: 12.5, margin: "6px 0 0" }}>Nothing is automated yet. Speed-to-lead and quote follow-up alone answer every lead in minutes and chase every quote for a week.</p>
        : <div className="autopilot-list">
            {on.slice(0, 8).map(a => (
              <span key={a.id} className="autopilot-item" title={a.who}><Icon name={a.icon} size={12} /><b>{a.name}</b><span className="muted"> → {a.who}</span></span>
            ))}
            {on.length > 8 && <span className="autopilot-item muted">+{on.length - 8} more</span>}
          </div>}
    </div>
  );
}

/* ---- Reports: the Monday-morning digest, in the app and one tap to email ---- */
function weekDigest(store: any) {
  const now = new Date();
  const weekAgo = Date.now() - 7 * DAY;
  const quotes = store.quotes || [];
  const wonLastWeek = quotes.filter((q: any) => WON.has(q.status) && (Number(q.signedAt || (q.signature && Date.parse(q.signature.at)) || q.paidAt || q.createdAt) || 0) >= weekAgo);
  const wonTotal = wonLastWeek.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
  const todos = (window as any).buildTodos ? (window as any).buildTodos(store) : [];
  const cold = todos.filter((t: any) => t.kind === "quote" && t.followup && t.followup.daysSince >= 3);
  const balances = todos.filter((t: any) => t.kind === "balance");
  const owed = balances.reduce((a: number, t: any) => a + (t.value || 0), 0);
  // this week = today through the next 7 days
  const ymd = (d: Date) => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  const from = ymd(now), to = ymd(new Date(Date.now() + 7 * DAY));
  const jobs = (store.workOrders || []).filter((w: any) => { const s = String(w.scheduledStart || "").slice(0, 10); return s && s >= from && s <= to && w.status !== "completed"; });
  const text = [
    `Week of ${now.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    `Won last week: ${money0(wonTotal)} (${wonLastWeek.length} job${wonLastWeek.length === 1 ? "" : "s"})`,
    `Quotes going cold: ${cold.length}${cold.length ? " — " + cold.slice(0, 3).map((t: any) => t.customer).join(", ") : ""}`,
    `Jobs this week: ${jobs.length}${jobs.length ? " — " + jobs.slice(0, 3).map((w: any) => (w.customer || w.title) + " " + String(w.scheduledStart).slice(5, 10)).join(", ") : ""}`,
    `Balances due: ${money0(owed)} (${balances.length})`,
  ].join("\n");
  return { wonLastWeek, wonTotal, cold, jobs, balances, owed, text };
}
function WeekDigest({ store, go }: any) {
  const d = React.useMemo(() => weekDigest(store), [store]);
  const me = (store.settings && store.settings.businessEmail) || "";
  const mailto = "mailto:" + encodeURIComponent(me) + "?subject=" + encodeURIComponent("Fence Works — this week") + "&body=" + encodeURIComponent(d.text);
  const copy = async () => { try { await navigator.clipboard.writeText(d.text); (window as any).VFP_NOTIFY && (window as any).VFP_NOTIFY("Copied"); } catch (e) {} };
  return (
    <div className="card pad digest-card">
      <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <span className="kicker">This week at a glance</span>
        <div className="row gap-8">
          <button className="btn btn-ghost btn-sm" onClick={copy}>Copy</button>
          <a className="btn btn-outline btn-sm" href={mailto}><Icon name="mail" size={13} />Email me this</a>
        </div>
      </div>
      <div className="digest-grid">
        <button className="digest-tile" onClick={() => go("estimates")}><b className="tnum" style={{ color: "var(--green)" }}>{money0(d.wonTotal)}</b><span>won last week · {d.wonLastWeek.length} job{d.wonLastWeek.length === 1 ? "" : "s"}</span></button>
        <button className="digest-tile" onClick={() => go("todo")}><b className="tnum" style={{ color: d.cold.length ? "var(--amber)" : undefined }}>{d.cold.length}</b><span>quote{d.cold.length === 1 ? "" : "s"} going cold</span></button>
        <button className="digest-tile" onClick={() => go("calendar")}><b className="tnum">{d.jobs.length}</b><span>job{d.jobs.length === 1 ? "" : "s"} this week</span></button>
        <button className="digest-tile" onClick={() => go("todo")}><b className="tnum" style={{ color: d.owed ? "var(--amber)" : undefined }}>{money0(d.owed)}</b><span>in balances due</span></button>
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>The same summary can land in your inbox every Monday once the owner-summary automation is on (AI Front Desk → Owner summary).</p>
    </div>
  );
}

Object.assign(window, { roiSummary, RoiPanel, AutopilotStrip, nextTouch, WeekDigest, AUTOMATION_WHO });

export { roiSummary, RoiPanel, AutopilotStrip, nextTouch, WeekDigest, AUTOMATION_WHO, activeAutomations, weekDigest, websiteCallsLine };
